<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { IdeaWithVotes } from '../types';
import Heart from './icons/Heart.vue';
import AlertCircle from './icons/AlertCircle.vue';
import HandHelping from './icons/HandHelping.vue';
import Edit from './icons/Edit.vue';
import Trash2 from './icons/Trash2.vue';
import MessageSquare from './icons/MessageSquare.vue';
import ConfirmDialog from './ConfirmDialog.vue';

interface Props {
  idea: IdeaWithVotes;
  visibleVoteTypes?: ('liked' | 'important' | 'supported')[];
  currentUserId?: string;
  userRole?: 'owner' | 'member' | 'reader' | null;
  votesLocked?: boolean;
  commentsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  visibleVoteTypes: () => ['liked', 'important', 'supported'],
  votesLocked: false,
  commentsEnabled: true,
});

const emit = defineEmits({
  vote: (
    _id: string,
    _type: 'liked' | 'important' | 'supported',
    _onError: (() => void) | undefined
  ) => true,
  edit: (_id: string) => true,
  delete: (_id: string) => true,
  tagClick: (_tag: string) => true,
  comment: (_id: string) => true,
});

const canEdit = computed(() => {
  if (props.userRole === 'owner') return true;
  if (props.userRole === 'member') {
    return props.idea.createdBy === props.currentUserId;
  }
  return false;
});

const voteStates = ref({
  liked: props.idea.userVotes?.liked ?? false,
  important: props.idea.userVotes?.important ?? false,
  supported: props.idea.userVotes?.supported ?? false,
});

const voteCounts = ref({
  liked: props.idea.liked,
  important: props.idea.important,
  supported: props.idea.supported,
});
const showDeleteConfirm = ref(false);

const totalScore = computed(() => {
  return props.visibleVoteTypes.reduce((sum, type) => sum + (voteCounts.value[type] || 0), 0);
});

function toggleVote(type: 'liked' | 'important' | 'supported') {
  // Check if votes are locked
  if (props.votesLocked) return;

  const currentHasVote = voteStates.value[type];
  const currentCount = voteCounts.value[type] ?? 0;
  const nextState = !currentHasVote;
  const nextCount = currentHasVote ? currentCount - 1 : currentCount + 1;

  voteStates.value = { ...voteStates.value, [type]: nextState };
  voteCounts.value = { ...voteCounts.value, [type]: nextCount };

  emit('vote', props.idea.id, type, () => {
    voteStates.value = { ...voteStates.value, [type]: currentHasVote };
    voteCounts.value = { ...voteCounts.value, [type]: currentCount };
  });
}

function handleTagClick(tag: string) {
  emit('tagClick', tag);
}

function requestDelete() {
  showDeleteConfirm.value = true;
}

function confirmDelete() {
  showDeleteConfirm.value = false;
  emit('delete', props.idea.id);
}

function cancelDelete() {
  showDeleteConfirm.value = false;
}

watch(
  () => props.idea,
  idea => {
    voteStates.value = {
      liked: idea.userVotes?.liked ?? false,
      important: idea.userVotes?.important ?? false,
      supported: idea.userVotes?.supported ?? false,
    };
    voteCounts.value = {
      liked: idea.liked,
      important: idea.important,
      supported: idea.supported,
    };
  },
  { immediate: true }
);
</script>

<template>
  <div class="rounded-lg border border-black/10 bg-white/90 p-3">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="font-semibold text-gray-800">{{ idea.title }}</div>
        <div v-if="idea.person" class="text-xs text-gray-500">
          {{ $t('idea.by') }} {{ idea.person }}
        </div>
      </div>
      <div class="flex flex-col items-end">
        <div class="text-xs font-semibold text-gray-700">Total {{ totalScore }}</div>
        <button
          v-if="(idea.commentCount || 0) > 0"
          @click.stop="$emit('comment', idea.id)"
          class="mt-1 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          :title="$t('comments.view')"
        >
          <MessageSquare class="w-3 h-3" />
          <span>{{ idea.commentCount }}</span>
        </button>
      </div>
    </div>

    <div class="mt-2 flex flex-wrap gap-1">
      <button
        v-for="tag in idea.tags"
        :key="tag"
        @click.stop="handleTagClick(tag)"
        class="px-2 py-0.5 text-xs rounded-full bg-black/5 text-gray-700 hover:bg-black/10 transition-colors capitalize"
      >
        {{ tag }}
      </button>
      <span v-if="idea.tags.length === 0" class="text-xs text-gray-400">No tags</span>
    </div>

    <div v-if="idea.explanation" class="mt-2 text-xs text-gray-600 line-clamp-3">
      {{ idea.explanation }}
    </div>

    <div class="mt-3">
      <div class="text-[11px] uppercase tracking-wide text-gray-500">Votes</div>
      <div class="mt-1 flex items-center gap-2 text-xs">
        <div v-if="visibleVoteTypes.includes('liked')" class="flex items-center gap-1">
          <Heart class="w-3.5 h-3.5 text-(--color-dot-liked)" />
          <span class="font-semibold">{{ voteCounts.liked }}</span>
        </div>
        <div v-if="visibleVoteTypes.includes('important')" class="flex items-center gap-1">
          <AlertCircle class="w-3.5 h-3.5 text-(--color-dot-important)" />
          <span class="font-semibold">{{ voteCounts.important }}</span>
        </div>
        <div v-if="visibleVoteTypes.includes('supported')" class="flex items-center gap-1">
          <HandHelping class="w-3.5 h-3.5 text-(--color-dot-supported)" />
          <span class="font-semibold">{{ voteCounts.supported }}</span>
        </div>
      </div>
    </div>

    <div class="mt-3">
      <div class="text-[11px] uppercase tracking-wide text-gray-500">Actions</div>
      <div class="mt-1 flex items-center justify-end gap-1">
        <button
          v-if="visibleVoteTypes.includes('liked')"
          @click.stop="toggleVote('liked')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-150 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : voteStates.liked
                ? 'bg-red-100 text-(--color-dot-liked)'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Like'"
        >
          <Heart class="w-4 h-4" />
        </button>
        <button
          v-if="visibleVoteTypes.includes('important')"
          @click.stop="toggleVote('important')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-150 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : voteStates.important
                ? 'bg-amber-100 text-(--color-dot-important)'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Important'"
        >
          <AlertCircle class="w-4 h-4" />
        </button>
        <button
          v-if="visibleVoteTypes.includes('supported')"
          @click.stop="toggleVote('supported')"
          :disabled="votesLocked"
          :class="[
            'p-1.5 rounded-md transition-all duration-150 flex items-center justify-center',
            votesLocked
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-50'
              : voteStates.supported
                ? 'bg-blue-100 text-(--color-dot-supported)'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200',
          ]"
          :title="votesLocked ? $t('workshop.votingLocked') : 'Toggle Support'"
        >
          <HandHelping class="w-4 h-4" />
        </button>
        <button
          v-if="commentsEnabled"
          @click.stop="$emit('comment', idea.id)"
          class="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-150"
          :title="$t('comments.add')"
        >
          <MessageSquare class="w-4 h-4" />
        </button>
        <button
          v-if="canEdit"
          @click.stop="emit('edit', idea.id)"
          class="p-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all duration-150"
          title="Edit Idea"
        >
          <Edit class="w-4 h-4" />
        </button>
        <button
          v-if="canEdit"
          @click.stop="requestDelete"
          class="p-1.5 rounded-md bg-gray-100 text-(--color-destructive) hover:bg-red-100 transition-all duration-150"
          title="Delete Idea"
        >
          <Trash2 class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
  <ConfirmDialog
    v-model="showDeleteConfirm"
    title="Delete Idea?"
    message="Are you sure you want to delete this idea? This action cannot be undone."
    confirm-text="Delete"
    cancel-text="Cancel"
    confirm-color="destructive"
    @confirm="confirmDelete"
    @cancel="cancelDelete"
  />
</template>
